/**
 * The standing suggestions (owner decision 2026-08-05): once the setup
 * questions are done — answered or waved off — the conversation never goes
 * dead. A quiet row of prompts sits above the composer whenever the doorman
 * is idle, each one a real thing it can do with the context it holds: the
 * recent knocks, the rules document, the proposal path. A tap DRAFTS the
 * prompt into the composer — the chat is the mechanism, as everywhere.
 *
 * "ABOVE THE COMPOSER" IS NOW LITERAL (F-18, 2026-09-13). This row used to be
 * the last thing in the log, which put it inside the box the Morning Nob is
 * laid over — and a briefing with items in it stands at that box's full
 * height, so all four chips were sealed behind the card with no press able to
 * reach them. It renders in the composer's own slot now, below the log and
 * outside the overlay, which is where the sentence above always said it was.
 */
/** Short pill, explicit drafted text: a small model follows the words in the
 * message far more reliably than any framing around it, so the prompt spells
 * out the pacing the pill only hints at. */
export type DoormanSuggestion = { label: string; prompt: string };

/**
 * THE SENTENCE THAT OPENS THE HOUSE RULES CONVERSATION, re-exported from the
 * package both tiers import (ruling R-0915-1). It used to live here, which was
 * fine while only the console said it; the api MATCHES on it — the onboarding
 * run is derived from the most recent copy of it in the thread — so a copy on
 * each side of the wire would drift in silence. The definition and the
 * reasoning are in `@nob/schemas` (`rules.ts`); this line is so the surfaces
 * that say it go on importing from one place.
 */
import { ONBOARDING_ASK } from "@nob/schemas";
export { ONBOARDING_ASK } from "@nob/schemas";

export const DOORMAN_SUGGESTIONS: readonly DoormanSuggestion[] = [
  { label: "What came in today?", prompt: "What came in today?" },
  { label: "Set up my house rules", prompt: ONBOARDING_ASK },
  { label: "What would you tighten?", prompt: "What would you tighten in my rules, and why?" },
  {
    label: "Which knocks did I leave hanging?",
    prompt: "Which knocks did I leave hanging?",
  },
];

type Props = {
  onPick: (prompt: string) => void;
  disabled: boolean;
};

export function DoormanSuggestions({ onPick, disabled }: Props) {
  return (
    <div className="mb-2 flex flex-wrap gap-2 pl-1" data-testid="doorman-suggestions">
      {DOORMAN_SUGGESTIONS.map(({ label, prompt }) => (
        <button
          key={label}
          type="button"
          disabled={disabled}
          onClick={() => onPick(prompt)}
          className="inline-flex h-8 items-center rounded-full border border-border px-3
                     text-xs text-muted transition-colors duration-75
                     hover:border-border-loud hover:text-text-soft
                     focus-visible:outline-none focus-visible:ring-2
                     focus-visible:ring-focus/70 focus-visible:ring-offset-2
                     focus-visible:ring-offset-bg disabled:pointer-events-none
                     disabled:opacity-40"
        >
          {label}
        </button>
      ))}
    </div>
  );
}
